import { useEffect, useState, type FormEvent } from "react";
import {
  AlertCircle,
  ArrowRight,
  BarChart3,
  ClipboardList,
  Eye,
  EyeOff,
  FlaskConical,
  Library,
  LoaderCircle,
  LockKeyhole,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import industrialMindLogo from "../../assets/industrialmind-logo.png";
import { LanguageSwitcher, useI18n } from "../../i18n";
import { useAuth } from "./AuthProvider";

const EVALUATION_STEPS = [
  { key: "questionBank", icon: Library },
  { key: "suites", icon: ClipboardList },
  { key: "runs", icon: FlaskConical },
  { key: "reports", icon: BarChart3 },
] as const;

export function LoginPage() {
  const auth = useAuth();
  const { t } = useI18n();
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const platformMode = auth.config.mode === "im_production_same_origin";

  useEffect(() => {
    document.title = t("shell.documentTitle", { page: t("shell.brandSubtitle") });
  }, [t]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage("");
    try {
      if (auth.bootstrap_required) await auth.bootstrap(username.trim(), password);
      else await auth.login(username.trim(), password);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t("auth.loginFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="react-auth-page">
      <section className="react-auth-intro">
        <div className="react-auth-intro-content">
          <div className="react-auth-lockup">
            <img src={industrialMindLogo} alt="" aria-hidden="true" />
            <span><strong>Agent Judge</strong><small>{t("auth.workspaceLabel")}</small></span>
          </div>
          <div className="react-auth-story">
            <span className="react-auth-kicker">{t("auth.kicker")}</span>
            <h1>{t("auth.headlineLead")}<br /><em>{t("auth.headlineEmphasis")}</em></h1>
            <p>{t("auth.headlineDescription")}</p>
          </div>
          <ol className="react-auth-flow" aria-label={t("auth.flowAria")}>
            {EVALUATION_STEPS.map((step) => <li key={step.key}>
              <span><step.icon size={20} aria-hidden="true" /></span>
              <strong>{t(`auth.step.${step.key}`)}</strong>
            </li>)}
          </ol>
          <p className="react-auth-powered"><ShieldCheck size={16} aria-hidden="true" />{t("auth.poweredBy")}</p>
        </div>
      </section>
      <section className="react-auth-form-panel" aria-label={t("auth.formPanelAria")}>
        <LanguageSwitcher className="react-auth-language" />
        <form className="react-auth-card" onSubmit={platformMode ? (event) => event.preventDefault() : submit}>
          <div className="react-auth-form-brand">
            <img className="react-brand-mark" src={industrialMindLogo} alt="IndustrialMind" />
            <strong>Agent Judge</strong>
          </div>
          <div className="react-auth-heading">
            <span className="eyebrow">{platformMode ? t("auth.platformEyebrow") : auth.bootstrap_required ? t("auth.bootstrapEyebrow") : t("auth.eyebrow")}</span>
            <h2>{platformMode ? t("auth.platformTitle") : auth.bootstrap_required ? t("auth.bootstrapTitle") : t("auth.title")}</h2>
            <p>{platformMode ? t("auth.platformDescription") : auth.bootstrap_required ? t("auth.bootstrapDescription") : t("auth.description")}</p>
          </div>
          {auth.loginNotice ? <p className="auth-session-notice" role="alert"><AlertCircle size={16} aria-hidden="true" />{auth.loginNotice}</p> : null}
          {platformMode ? <>
            <p className="auth-platform-guidance" role="status">{t("auth.platformGuidance")}</p>
            <a className="primary-button auth-submit" href={auth.config.login_url || "/login"} target="_blank" rel="noopener noreferrer">
              {t("auth.platformOpenLogin")}<ArrowRight size={17} aria-hidden="true" />
            </a>
            <button className="secondary-button auth-submit" type="button" onClick={() => void auth.refresh()}>
              {t("auth.platformRecheck")}
            </button>
          </> : <>
            <div className="auth-field">
              <label htmlFor="login-username">{t("auth.username")}</label>
              <div className="input-with-icon"><UserRound size={18} aria-hidden="true" /><input id="login-username" value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" aria-invalid={Boolean(message)} aria-describedby={message ? "login-error" : undefined} /></div>
            </div>
            <div className="auth-field">
              <label htmlFor="login-password">{t("auth.password")}</label>
              <div className="input-with-icon">
                <LockKeyhole size={18} aria-hidden="true" />
                <input id="login-password" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={auth.bootstrap_required ? "new-password" : "current-password"} aria-invalid={Boolean(message)} aria-describedby={message ? "login-error" : undefined} />
                <button className="auth-password-toggle" type="button" aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")} aria-pressed={showPassword} onClick={() => setShowPassword((visible) => !visible)}>
                  {showPassword ? <EyeOff size={17} aria-hidden="true" /> : <Eye size={17} aria-hidden="true" />}
                </button>
              </div>
            </div>
            {message ? <p id="login-error" className="form-error" role="alert"><AlertCircle size={16} aria-hidden="true" />{message}</p> : null}
            <button className="primary-button auth-submit" type="submit" disabled={submitting || !username.trim() || !password}>
              {submitting ? <><LoaderCircle className="spin" size={17} aria-hidden="true" />{t("auth.verifying")}</> : <>{auth.bootstrap_required ? t("auth.bootstrapSubmit") : t("auth.submit")}<ArrowRight size={17} aria-hidden="true" /></>}
            </button>
          </>}
        </form>
        <p className="react-auth-trust"><ShieldCheck size={15} aria-hidden="true" />{t(platformMode ? "auth.platformTrustNote" : "auth.trustNote")}</p>
      </section>
    </main>
  );
}
